import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Business as BusinessIcon,
} from '@mui/icons-material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';
import { NotesSection } from '../../../components/accounting/NotesSection';

interface LineItem {
  lineNumber: number;
  lineId?: string;
  detailType?: string;
  amount?: number;
  description?: string;
  item?: { value?: string; name?: string } | null;
  unitPrice?: number;
  quantity?: number;
  taxCode?: { value?: string; name?: string } | null;
  serviceDate?: string;
  discount?: {
    rate?: number;
    amount?: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface LinkedBill {
  id: string;
  docNumber?: string;
  vendor?: {
    id?: string;
    name?: string;
  };
  transactionDate?: string;
  dueDate?: string;
  totalAmount?: number;
  balance?: number;
  currencyCode?: string;
  currencyName?: string;
  mappingReason?: string;
  mappingCreatedAt?: string;
  mappingCreatedBy?: string;
}

interface Subsidiary {
  vendorId: string;
  name: string;
}

interface InvoiceDetails {
  id: string;
  syncToken?: string;
  docNumber?: string;
  transactionDate?: string;
  dueDate?: string;
  shipDate?: string;
  trackingNumber?: string;
  status?: string;
  customer?: {
    id?: string;
    name?: string;
    displayName?: string;
    companyName?: string;
    email?: string;
    phone?: string;
    balance?: number;
    billEmail?: string;
  };
  financial?: {
    totalAmount?: number;
    homeTotalAmount?: number;
    balance?: number;
    homeBalance?: number;
    deposit?: number;
    currencyCode?: string;
    currencyName?: string;
    exchangeRate?: number;
  };
  references?: {
    department?: { value?: string; name?: string } | null;
    salesTerms?: { value?: string; name?: string } | null;
    depositToAccount?: { value?: string; name?: string } | null;
  };
  statusDetails?: {
    printStatus?: string;
    emailStatus?: string;
    applyTaxAfterDiscount?: boolean;
    syncStatus?: string;
    lastSyncAt?: string;
    syncError?: string;
  };
  notes?: {
    privateNote?: string;
    customerMemo?: string;
  };
  subsidiary?: Subsidiary | null;
  metadata?: {
    quickBooksCreateTime?: string;
    quickBooksLastUpdatedTime?: string;
    platformCreatedAt?: string;
    platformUpdatedAt?: string;
  };
  lineItems?: LineItem[];
  linkedBills?: LinkedBill[];
}

export const InvoiceDetailsPage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();

  const [invoice, setInvoice] = useState<InvoiceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [billInfoModalOpen, setBillInfoModalOpen] = useState(false);
  const [invoiceInfoModalOpen, setInvoiceInfoModalOpen] = useState(false);

  const fetchInvoiceDetails = async () => {
    if (!invoiceId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/accounting/admin/invoices/${invoiceId}`);

      if (!response || !response.ok) {
        if (response?.status === 403) {
          throw new Error('Access denied. You do not have permission to view this invoice.');
        }
        if (response?.status === 404) {
          throw new Error('Invoice not found');
        }
        throw new Error('Failed to fetch invoice details');
      }

      const result = await response.json();
      setInvoice(result.data);
    } catch (err: any) {
      console.error('Error fetching invoice details:', err);
      setError(err.message || 'Failed to fetch invoice details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, [invoiceId]);

  const formatCurrency = (amount?: number, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const handleDownloadPdf = async () => {
    if (!invoiceId) return;

    setDownloadingPdf(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/accounting/admin/invoices/${invoiceId}/pdf`);

      if (!response || !response.ok) {
        // Check for 403 access denied first
        if (response?.status === 403) {
          alert('Access denied. You do not have permission to download this invoice PDF.');
          return;
        }

        // Try to get error details from response
        let errorMessage = 'Failed to download invoice PDF';
        if (response) {
          try {
            const errorData = await response.json();
            if (errorData.message) {
              errorMessage = errorData.message;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
            if (errorData.solution) {
              errorMessage += '\n\n' + errorData.solution;
            }
          } catch {
            // If parsing JSON fails, use default message
          }
        }
        throw new Error(errorMessage);
      }

      // Get the PDF blob
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoice?.docNumber || invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading invoice PDF:', err);
      alert(err.message || 'Failed to download invoice PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          No invoice data available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        {hasPermission('accounting.admin') && (
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/hub/accounting/invoices')}
          >
            Invoices
          </Link>
        )}
        <Typography color="text.primary">
          Invoice {invoice.docNumber || invoice.id}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <ReceiptIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h1">
                Horwath HTL Invoice # {invoice.docNumber || invoice.id}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setInvoiceInfoModalOpen(true)}
                sx={{ color: 'info.main' }}
              >
                <InfoIcon />
              </IconButton>
            </Box>
          </Box>
          <Button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            color="primary"
            variant="contained"
            startIcon={<DownloadIcon />}
          >
            Download Invoice PDF
          </Button>
        </Box>
      </Box>

      {/* Main Invoice Document */}
      <Paper sx={{ p: 4, mb: 3 }}>
        {/* Invoice Header */}
        <Box sx={{ borderBottom: '3px solid #1976d2', pb: 3, mb: 3 }}>
          <Grid container>
            <Grid item xs={6}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Invoice #{invoice.docNumber || invoice.id}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: 'right' }}>
              <Box sx={{ mb: 2 }}>
                {invoice.status === 'paid' ? (
                  <Chip label="PAID" color="success" size="medium" sx={{ fontSize: '1.1rem', px: 2, py: 0.5 }} />
                ) : invoice.status === 'void' ? (
                  <Chip label="VOID" color="default" size="medium" sx={{ fontSize: '1.1rem', px: 2, py: 0.5 }} />
                ) : (
                  <Chip label="OPEN" color="warning" size="medium" sx={{ fontSize: '1.1rem', px: 2, py: 0.5 }} />
                )}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatCurrency(invoice.financial?.totalAmount, invoice.financial?.currencyCode)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Balance: {formatCurrency(invoice.financial?.balance, invoice.financial?.currencyCode)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Customer and Invoice Details */}
        <Grid container spacing={4} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>BILL TO</Typography>
            <Box>
              <Link
                component="button"
                variant="h6"
                onClick={() => navigate(`/admin/hub/accounting/customers/${invoice.customer?.id}`)}
                sx={{ textAlign: 'left', display: 'block', mb: 0.5 }}
              >
                {invoice.customer?.displayName || invoice.customer?.name}
              </Link>
              {invoice.customer?.companyName && invoice.customer.companyName !== invoice.customer.displayName && (
                <Typography variant="body2">{invoice.customer.companyName}</Typography>
              )}
              {invoice.customer?.email && (
                <Typography variant="body2" color="text.secondary">{invoice.customer.email}</Typography>
              )}
              {invoice.customer?.phone && (
                <Typography variant="body2" color="text.secondary">{invoice.customer.phone}</Typography>
              )}
              {invoice.customer?.billEmail && invoice.customer.billEmail !== invoice.customer.email && (
                <Typography variant="body2" color="text.secondary">Bill Email: {invoice.customer.billEmail}</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>INVOICE DETAILS</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Invoice Date:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">{formatDate(invoice.transactionDate)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Due Date:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  {formatDate(invoice.dueDate)}
                </Typography>
              </Grid>
              {invoice.references?.salesTerms?.name && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Terms:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">{invoice.references.salesTerms.name}</Typography>
                  </Grid>
                </>
              )}
              {invoice.shipDate && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Ship Date:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">{formatDate(invoice.shipDate)}</Typography>
                  </Grid>
                </>
              )}
              {invoice.trackingNumber && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Tracking #:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{invoice.trackingNumber}</Typography>
                  </Grid>
                </>
              )}
              {invoice.references?.department?.name && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Department:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">{invoice.references.department.name}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

        {/* Line Items */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>LINE ITEMS</Typography>
          {invoice.lineItems && invoice.lineItems.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.lineItems.map((item, index) => (
                    <TableRow key={`line-item-${item.lineId || ''}-${index}`}>
                      <TableCell>{item.lineNumber}</TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>{item.item?.name || '-'}</TableCell>
                      <TableCell align="right">
                        {item.quantity !== undefined && item.quantity !== null ? item.quantity : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {item.unitPrice !== undefined && item.unitPrice !== null
                          ? formatCurrency(item.unitPrice, invoice.financial?.currencyCode)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.amount, invoice.financial?.currencyCode)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No line items found
            </Typography>
          )}
        </Box>

        {/* Financial Summary */}
        <Box sx={{ borderTop: '2px solid', borderColor: 'divider', pt: 2 }}>
          <Grid container>
            <Grid item xs={12} md={6}>
              {invoice.financial?.exchangeRate && invoice.financial.exchangeRate !== 1 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Exchange Rate: {invoice.financial.exchangeRate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Home Currency: {formatCurrency(invoice.financial?.homeTotalAmount)}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'right' }}>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={6}>
                    <Typography variant="body1">Subtotal:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1">
                      {formatCurrency(invoice.financial?.totalAmount, invoice.financial?.currencyCode)}
                    </Typography>
                  </Grid>
                  {invoice.financial?.deposit !== undefined && invoice.financial.deposit > 0 && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body1">Deposit:</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body1" color="success.main">
                          -{formatCurrency(invoice.financial.deposit, invoice.financial?.currencyCode)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Balance Due:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: invoice.financial?.balance === 0 ? 'success.main' :
                               invoice.financial?.balance && invoice.financial.balance > 0 ? 'error.main' :
                               'text.primary'
                      }}
                    >
                      {formatCurrency(invoice.financial?.balance, invoice.financial?.currencyCode)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Customer Memo */}
        {invoice.notes?.customerMemo && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>CUSTOMER MEMO</Typography>
            <Typography variant="body2">{invoice.notes.customerMemo}</Typography>
          </Box>
        )}

        {/* Private Note */}
        {invoice.notes?.privateNote && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>PRIVATE NOTE</Typography>
            <Typography variant="body2">{invoice.notes.privateNote}</Typography>
          </Box>
        )}

        {/* Subsidiary Association */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <BusinessIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle2" color="text.secondary">SUBSIDIARY</Typography>
          </Box>
          {invoice.subsidiary ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                label={invoice.subsidiary.name}
                color="primary"
                variant="outlined"
                onClick={() => navigate(`/admin/hub/accounting/vendors/${invoice.subsidiary?.vendorId}`)}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No subsidiary associated with this invoice.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Corresponding Horwath HTL Bill */}
      {invoice.linkedBills && invoice.linkedBills.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LinkIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Corresponding Horwath HTL Bill</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bill #</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.linkedBills.map((bill, index) => (
                  <TableRow key={`linked-bill-${bill.id}-${index}`}>
                    <TableCell>
                      <Chip
                        label={bill.docNumber || bill.id}
                        onClick={() => navigate(`/admin/hub/accounting/bills/${bill.id}`)}
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/admin/hub/accounting/vendors/${bill.vendor?.id}`)}
                      >
                        {bill.vendor?.name}
                      </Link>
                    </TableCell>
                    <TableCell>{formatDate(bill.transactionDate)}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(bill.totalAmount, bill.currencyCode)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          color: bill.balance === 0 ? 'green' :
                                 bill.balance && bill.balance > 0 ? 'error.main' :
                                 'text.primary'
                        }}
                      >
                        {formatCurrency(bill.balance, bill.currencyCode)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Notes Section */}
      {invoiceId && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <NotesSection
            entityType="invoice"
            entityId={invoiceId}
          />
        </Paper>
      )}

      {/* Bill Information Modal */}
      <Dialog
        open={billInfoModalOpen}
        onClose={() => setBillInfoModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>About Internal Bills</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            This internal bill serves as a payment placeholder for the corresponding Horwath HTL invoice. Horwath will pay this bill once the invoice payment has been received. The bill amount should match the subsidiary invoice which can be found/viewed below.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillInfoModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Information Modal */}
      <Dialog
        open={invoiceInfoModalOpen}
        onClose={() => setInvoiceInfoModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>About This Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            This is the Horwath HTL invoice issued to the customer. Upon receipt of payment, the corresponding internal bill (shown below) will be paid.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceInfoModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};